/**
 * Story video
 *
 * A single video with the smallest player that still feels complete: play, pause, sound and a timeline to scrub.
 *
 * The page only loads the cover image; the video file itself is fetched on the first play (preload="none"), so a
 * section that most visitors scroll past costs them nothing. Playback stops when the video scrolls out of view.
 */
(function () {
  if (customElements.get('story-video')) return;

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function formatTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) seconds = 0;
    const minutes = Math.floor(seconds / 60);
    const rest = Math.floor(seconds % 60);
    return minutes + ':' + String(rest).padStart(2, '0');
  }

  class StoryVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('[data-story-video]');
      if (!this.video || this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.playButton = this.querySelector('[data-story-video-play]');
      this.toggle = this.querySelector('[data-story-video-toggle]');
      this.sound = this.querySelector('[data-story-video-sound]');
      this.timeline = this.querySelector('[data-story-video-timeline]');
      this.progress = this.querySelector('[data-story-video-progress]');
      this.time = this.querySelector('[data-story-video-time]');
      this.volumeInput = this.querySelector('[data-story-video-volume]');

      this.defaultVolume = clamp((Number(this.getAttribute('volume')) || 70) / 100, 0, 1);
      this.video.volume = this.defaultVolume;
      this.renderVolume();

      const togglePlayback = () => {
        if (this.video.paused) {
          this.video.play().catch(() => {});
        } else {
          this.video.pause();
        }
      };

      this.playButton?.addEventListener('click', togglePlayback);
      this.toggle?.addEventListener('click', togglePlayback);
      this.video.addEventListener('click', togglePlayback);

      this.sound?.addEventListener('click', () => {
        this.video.muted = !this.video.muted;
        /* Unmuting a video whose volume was dragged to zero has to give it something to play. */
        if (!this.video.muted && this.video.volume === 0) this.video.volume = this.defaultVolume;
      });

      /* Dragging to zero mutes, dragging back up unmutes, so the button and the slider always agree. */
      this.volumeInput?.addEventListener('input', () => {
        const level = clamp(Number(this.volumeInput.value) / 100, 0, 1);
        this.video.volume = level;
        this.video.muted = level === 0;
      });

      this.video.addEventListener('volumechange', () => {
        this.sound?.setAttribute('aria-pressed', String(this.video.muted));
        this.renderVolume();
      });

      this.video.addEventListener('play', () => {
        this.classList.add('is-playing');
        this.classList.remove('is-paused');
        this.pauseOtherPlayers();
      });

      this.video.addEventListener('pause', () => {
        this.classList.remove('is-playing');
        if (this.video.currentTime > 0 && !this.video.ended) this.classList.add('is-paused');
      });

      this.video.addEventListener('ended', () => {
        this.classList.remove('is-playing', 'is-paused');
        this.render(0);
      });

      this.video.addEventListener('timeupdate', () => {
        if (this.scrubbing) return;
        this.render();
      });

      if (this.timeline) this.setupTimeline();

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (!entries[0].isIntersecting && !this.video.paused) this.video.pause();
        }, { threshold: 0.3 });
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    setupTimeline() {
      const seekTo = (clientX) => {
        const rect = this.timeline.getBoundingClientRect();
        if (!rect.width || !isFinite(this.video.duration)) return;

        const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
        this.video.currentTime = ratio * this.video.duration;
        this.render(ratio);
      };

      this.timeline.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        this.scrubbing = true;
        this.timeline.setPointerCapture(event.pointerId);
        seekTo(event.clientX);
      });

      this.timeline.addEventListener('pointermove', (event) => {
        if (this.scrubbing) seekTo(event.clientX);
      });

      const endScrub = (event) => {
        if (!this.scrubbing) return;
        this.scrubbing = false;
        if (this.timeline.hasPointerCapture?.(event.pointerId)) this.timeline.releasePointerCapture(event.pointerId);
      };

      this.timeline.addEventListener('pointerup', endScrub);
      this.timeline.addEventListener('pointercancel', endScrub);

      this.timeline.addEventListener('keydown', (event) => {
        if (!isFinite(this.video.duration)) return;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.video.currentTime = Math.max(0, this.video.currentTime - 5);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.video.currentTime = Math.min(this.video.duration, this.video.currentTime + 5);
        } else if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          if (this.video.paused) this.video.play().catch(() => {});
          else this.video.pause();
        }
      });
    }

    render(forcedRatio) {
      const duration = this.video.duration;
      const ratio = forcedRatio !== undefined
        ? forcedRatio
        : (isFinite(duration) && duration > 0 ? this.video.currentTime / duration : 0);

      if (this.progress) this.progress.style.width = (ratio * 100).toFixed(2) + '%';
      if (this.timeline) this.timeline.setAttribute('aria-valuenow', Math.round(ratio * 100));
      if (this.time) this.time.textContent = formatTime(isFinite(duration) ? duration - this.video.currentTime : 0);
    }

    /** Keeps the slider, and the white part of its track, in step with the video's own volume. */
    renderVolume() {
      if (!this.volumeInput) return;

      const level = this.video.muted ? 0 : this.video.volume;
      this.volumeInput.value = String(Math.round(level * 100));
      this.volumeInput.style.setProperty('--story-video-volume', (level * 100).toFixed(0) + '%');
    }

    /** Two players on one page should never talk over each other, including the product videos. */
    pauseOtherPlayers() {
      document.querySelectorAll('video').forEach((other) => {
        if (other !== this.video && !other.paused) other.pause();
      });
    }
  }

  customElements.define('story-video', StoryVideo);
})();
